import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Megaphone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useBranches } from '@/hooks/api/branches';
import { MediaUploadField } from '@/components/media/MediaUploadField';
import {
  useCashbackOffers, useEligibleCategories, useMarketingApplications,
  useMarketingMutation, useMerchantPromotions,
} from '@/hooks/use-marketing';
import {
  PROMOTION_TYPES, applicationStatusLabel, applicationStatusVariant, bpsToPercent,
  cashbackStatusLabel, cashbackStatusVariant, formatPeriod, isLive, percentToBps,
  promotionDescription, promotionLabel, type ApplicationStatus, type CashbackStatus, type PromotionType,
} from '@/lib/marketing';

const emptyForm = {
  promotion_type: 'VIP_STORES' as PromotionType,
  category_id: '',
  requested_start_at: '',
  requested_end_at: '',
  promo_title: '',
  promo_image_url: '',
  merchant_comment: '',
  cashback_percent: '',
  branch_ids: [] as string[],
  terms: '',
};

export default function MarketingPage() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  // Har bir ariza uchun alohida rasm yo'li
  const [draftRef, setDraftRef] = useState(() => crypto.randomUUID());

  const { data: applications = [] } = useMarketingApplications();
  const { data: promotions = [] } = useMerchantPromotions();
  const { data: offers = [] } = useCashbackOffers();
  const { data: categories = [] } = useEligibleCategories();
  const { data: branches = [] } = useBranches();

  const submit = useMarketingMutation('submit_application');
  const withdraw = useMarketingMutation('withdraw_application');
  const requestCancel = useMarketingMutation('request_cancellation');

  const start = (pt: PromotionType) => {
    setForm({ ...emptyForm, promotion_type: pt });
    setDraftRef(crypto.randomUUID());
    setOpen(true);
  };

  const hasPending = (pt: PromotionType) =>
    applications.some((a: any) => a.promotion_type === pt && ['DRAFT', 'SUBMITTED', 'UNDER_REVIEW'].includes(a.status));

  const handleSubmit = () => {
    if (!form.requested_start_at || !form.requested_end_at) return toast.error(t('mkt_page.enterPeriod'));
    if (new Date(form.requested_start_at) >= new Date(form.requested_end_at)) return toast.error(t('mkt_page.dateOrder'));
    if (form.promotion_type === 'PROMOTED_CATEGORIES' && !form.category_id) return toast.error(t('mkt_page.selectCategory'));
    let bps: number | undefined;
    if (form.promotion_type === 'BIG_CASHBACK') {
      bps = percentToBps(form.cashback_percent);
      if (!bps || bps <= 0 || bps > 10000) return toast.error(t('mkt_page.cashbackInvalid'));
    }
    submit.mutate(
      {
        promotion_type: form.promotion_type,
        category_id: form.category_id || undefined,
        requested_start_at: new Date(form.requested_start_at).toISOString(),
        requested_end_at: new Date(form.requested_end_at).toISOString(),
        promo_title: form.promo_title,
        promo_image_url: form.promo_image_url,
        merchant_comment: form.merchant_comment,
        proposed_cashback_bps: bps,
        branch_ids: form.branch_ids,
        terms: form.terms,
      },
      {
        onSuccess: () => { toast.success(t('mkt_page.submitted')); setOpen(false); setForm(emptyForm); },
        onError: (e: any) => toast.error(e.message),
      },
    );
  };

  const activePromotions = promotions.filter((p: any) =>
    isLive(p.campaign?.status, p.campaign?.start_at, p.campaign?.end_at) && p.is_active);
  const pastPromotions = promotions.filter((p: any) => !activePromotions.includes(p));

  return (
    <div className="mx-auto max-w-6xl animate-fade-in p-6">
      <div className="mb-5 flex items-center gap-2">
        <Megaphone size={18} className="text-primary" />
        <h1 className="text-xl font-semibold text-foreground">{t('mkt_page.title')}</h1>
      </div>
      <p className="mb-5 text-sm text-muted-foreground">
        {t('mkt_page.intro')}
      </p>

      <Tabs defaultValue="opportunities">
        <TabsList>
          <TabsTrigger value="opportunities">{t('mkt_page.tabOpportunities')}</TabsTrigger>
          <TabsTrigger value="applications">{t('mkt_page.tabApplications')} ({applications.length})</TabsTrigger>
          <TabsTrigger value="active">{t('mkt_page.tabActive')} ({activePromotions.length})</TabsTrigger>
          <TabsTrigger value="cashback">{t('mkt_page.tabCashback')} ({offers.length})</TabsTrigger>
          <TabsTrigger value="history">{t('mkt_page.tabHistory')}</TabsTrigger>
        </TabsList>

        <TabsContent value="opportunities" className="mt-4 grid gap-3 sm:grid-cols-2">
          {PROMOTION_TYPES.map((pt) => (
            <div key={pt} className="rounded-xl border border-border bg-card p-4">
              <p className="text-sm font-medium text-foreground">{promotionLabel[pt]}</p>
              <p className="mt-1 text-xs text-muted-foreground">{promotionDescription[pt]}</p>
              <Button className="mt-3" size="sm" disabled={hasPending(pt)} onClick={() => start(pt)}>
                {hasPending(pt) ? t('mkt_page.underReview') : t('mkt_page.apply')}
              </Button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="applications" className="mt-4">
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_page.colType')}</th>
                  <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_page.colPeriod')}</th>
                  <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_page.colStatus')}</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {applications.length === 0 ? (
                  <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">{t('mkt_page.noApplications')}</td></tr>
                ) : applications.map((a: any) => (
                  <tr key={a.id} className="border-b border-border last:border-0">
                    <td className="p-3">
                      <p className="text-sm text-foreground">{promotionLabel[a.promotion_type as PromotionType]}</p>
                      {a.category?.name && <p className="text-xs text-muted-foreground">{a.category.name}</p>}
                      {a.rejection_reason && <p className="text-xs text-destructive">{t('mkt_page.reason', { reason: a.rejection_reason })}</p>}
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">{formatPeriod(a.requested_start_at, a.requested_end_at)}</td>
                    <td className="p-3"><Badge variant={applicationStatusVariant[a.status as ApplicationStatus]}>{applicationStatusLabel[a.status as ApplicationStatus]}</Badge></td>
                    <td className="p-3 text-right">
                      {['DRAFT', 'SUBMITTED', 'UNDER_REVIEW'].includes(a.status) && (
                        <Button size="sm" variant="outline" onClick={() => withdraw.mutate({ id: a.id }, {
                          onSuccess: () => toast.success(t('mkt_page.withdrawn')),
                          onError: (e: any) => toast.error(e.message),
                        })}>{t('mkt_page.withdraw')}</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="active" className="mt-4 space-y-3">
          {activePromotions.length === 0 ? (
            <p className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">{t('mkt_page.noActive')}</p>
          ) : activePromotions.map((p: any) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
              <div>
                <p className="text-sm font-medium text-foreground">
                  {promotionLabel[p.campaign?.promotion_type as PromotionType]}
                  {p.category?.name ? ` · ${p.category.name}` : ''}
                </p>
                <p className="text-xs text-muted-foreground">
                  {p.campaign?.name} · {formatPeriod(p.campaign?.start_at, p.campaign?.end_at)}
                </p>
                {p.cashback_offer?.cashback_bps && (
                  <p className="text-xs text-primary">{bpsToPercent(p.cashback_offer.cashback_bps)} {t('mkt_page.cashbackWord')}</p>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={() => requestCancel.mutate({ item_id: p.id, reason: 'Do\'kon so\'rovi' }, {
                onSuccess: () => toast.success(t('mkt_page.cancelRequested')),
                onError: (e: any) => toast.error(e.message),
              })}>{t('mkt_page.requestCancel')}</Button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="cashback" className="mt-4 space-y-3">
          {offers.length === 0 ? (
            <p className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">{t('mkt_page.noOffers')}</p>
          ) : offers.map((o: any) => (
            <div key={o.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card p-4">
              <div>
                <p className="text-sm font-medium text-foreground">{bpsToPercent(o.cashback_bps)} {t('mkt_page.cashbackWord')}</p>
                <p className="text-xs text-muted-foreground">
                  {o.scope_type === 'MERCHANT' ? t('mkt_page.wholeStore') : t('mkt_page.selectedBranches')} · {formatPeriod(o.start_at, o.end_at)}
                </p>
              </div>
              <Badge variant={cashbackStatusVariant[o.status as CashbackStatus]}>{cashbackStatusLabel[o.status as CashbackStatus]}</Badge>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="history" className="mt-4 space-y-3">
          {pastPromotions.length === 0 ? (
            <p className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">{t('mkt_page.historyEmpty')}</p>
          ) : pastPromotions.map((p: any) => (
            <div key={p.id} className="rounded-xl border border-border bg-card p-4">
              <p className="text-sm text-foreground">{promotionLabel[p.campaign?.promotion_type as PromotionType]}</p>
              <p className="text-xs text-muted-foreground">
                {p.campaign?.name} · {formatPeriod(p.campaign?.start_at, p.campaign?.end_at)}
              </p>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t('mkt_page.dialogTitle', { type: promotionLabel[form.promotion_type] })}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {form.promotion_type === 'PROMOTED_CATEGORIES' && (
              <div>
                <Label>{t('mkt_page.category')}</Label>
                <Select value={form.category_id || '__none__'} onValueChange={(v) => setForm({ ...form, category_id: v === '__none__' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder={t('mkt_page.selectCategory')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t('mkt_page.notSelected')}</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {categories.length === 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">{t('mkt_page.categoryHint')}</p>
                )}
              </div>
            )}

            {form.promotion_type === 'BIG_CASHBACK' && (
              <>
                <div>
                  <Label>{t('mkt_page.cashbackPercent')}</Label>
                  <Input type="number" step="0.5" min="0.5" max="100" value={form.cashback_percent}
                    onChange={(e) => setForm({ ...form, cashback_percent: e.target.value })} placeholder={t('mkt_page.cashbackExample')} />
                </div>
                <div>
                  <Label>{t('mkt_page.branchesLabel')}</Label>
                  <div className="mt-1 max-h-32 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
                    {branches.map((b: any) => (
                      <label key={b.id} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.branch_ids.includes(b.id)}
                          onChange={(e) => setForm({
                            ...form,
                            branch_ids: e.target.checked ? [...form.branch_ids, b.id] : form.branch_ids.filter((x) => x !== b.id),
                          })} />
                        {b.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>{t('mkt_page.terms')}</Label>
                  <Textarea rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} />
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('mkt_page.start')}</Label>
                <Input type="date" value={form.requested_start_at} onChange={(e) => setForm({ ...form, requested_start_at: e.target.value })} />
              </div>
              <div>
                <Label>{t('mkt_page.end')}</Label>
                <Input type="date" value={form.requested_end_at} onChange={(e) => setForm({ ...form, requested_end_at: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>{t('mkt_page.promoTitle')}</Label>
              <Input value={form.promo_title} onChange={(e) => setForm({ ...form, promo_title: e.target.value })} />
            </div>
            <MediaUploadField
              kind="marketing_image"
              refId={draftRef}
              value={form.promo_image_url}
              onChange={(url) => setForm({ ...form, promo_image_url: url })}
              label={t('mkt_page.promoImage')}
            />
            <div>
              <Label>{t('mkt_page.comment')}</Label>
              <Textarea rows={2} value={form.merchant_comment} onChange={(e) => setForm({ ...form, merchant_comment: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('mkt_page.cancel')}</Button>
            <Button onClick={handleSubmit} disabled={submit.isPending}>{t('mkt_page.send')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
